import React, { useEffect, useMemo, useState } from "react";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import {
  forgotPasswordRequest,
  registerRequest,
} from "./authApi";

function buildCaptcha() {
  const a = Math.floor(Math.random() * 8) + 2;
  const b = Math.floor(Math.random() * 8) + 2;
  return { prompt: `${a} + ${b}`, answer: String(a + b) };
}

function passwordStrength(password) {
  let score = 0;
  if (password.length >= 8) score += 1;
  if (/[A-Z]/.test(password)) score += 1;
  if (/[a-z]/.test(password)) score += 1;
  if (/[0-9]/.test(password)) score += 1;
  if (/[^A-Za-z0-9]/.test(password)) score += 1;

  if (score <= 2) return { label: "Needs improvement", className: "is-weak" };
  if (score <= 4) return { label: "Good", className: "is-medium" };
  return { label: "Strong", className: "is-strong" };
}

function firstNameOf(name) {
  return (name || "there").trim().split(/\s+/)[0] || "there";
}

export function AuthExperience({ mode = "modal", onClose, onSuccess }) {
  const { login, socialProviders, user, updateUser } = useAuth();
  const { loadServerCart } = useCart();
  const [tab, setTab] = useState("login");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [loginData, setLoginData] = useState({ login_id: "", password: "" });
  const [registerData, setRegisterData] = useState({
    full_name: "",
    contact_number: "",
    email: "",
    username: "",
    password: "",
    password_confirmation: "",
    accept_terms: false,
  });
  const [captcha, setCaptcha] = useState(buildCaptcha());
  const strength = useMemo(() => passwordStrength(registerData.password), [registerData.password]);
  const visibleSocialProviders = useMemo(
    () => Object.entries(socialProviders || {}).filter(([, provider]) => provider?.enabled && provider?.url),
    [socialProviders]
  );

  useEffect(() => {
    if (!user || mode !== "page") {
      return;
    }

    setMessage(`You're already signed in as ${firstNameOf(user.full_name)}.`);
  }, [user, mode]);

  const resetFeedback = () => {
    setError("");
    setMessage("");
  };

  const refreshCaptcha = () => setCaptcha(buildCaptcha());

  const validateCaptcha = () => {
    const currentValue = (document.getElementById(`auth-captcha-${mode}`)?.value || "").trim();
    return currentValue === captcha.answer;
  };

  const resetAuthSurface = (nextTab = tab) => {
    resetFeedback();
    setShowForgot(false);
    setTab(nextTab);
    refreshCaptcha();
    const captchaInput = document.getElementById(`auth-captcha-${mode}`);
    if (captchaInput) captchaInput.value = "";
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    resetFeedback();

    if (!validateCaptcha()) {
      setError("Please answer the security check correctly.");
      refreshCaptcha();
      return;
    }

    setBusy(true);
    const result = await login(loginData.login_id, loginData.password);
    setBusy(false);

    if (!result.ok) {
      setError(result.message || "Login failed.");
      refreshCaptcha();
      return;
    }

    await loadServerCart();
    if (onSuccess) {
      await onSuccess();
    }
    if (onClose) {
      onClose();
    }
  };

  const handleRegister = async (event) => {
    event.preventDefault();
    resetFeedback();

    if (!registerData.accept_terms) {
      setError("Please accept the Terms & Conditions and Privacy Policy to continue.");
      return;
    }

    if (!validateCaptcha()) {
      setError("Please answer the security check correctly.");
      refreshCaptcha();
      return;
    }

    setBusy(true);
    try {
      const response = await registerRequest(registerData);
      if (response.status !== "success") {
        setError(response.message || "Registration failed.");
        refreshCaptcha();
        return;
      }

      const loginResult = await login(registerData.email, registerData.password);
      if (loginResult.ok) {
        updateUser(loginResult.data || null);
        await loadServerCart();
        if (onSuccess) {
          await onSuccess();
        }
        if (onClose) {
          onClose();
        }
      } else {
        setMessage("Your account has been created. Please log in to continue shopping.");
        setTab("login");
      }
    } catch (requestError) {
      console.error("Registration error:", requestError);
      setError("We couldn't complete registration right now. Please try again.");
      refreshCaptcha();
    } finally {
      setBusy(false);
    }
  };

  const handleForgotPassword = async (event) => {
    event.preventDefault();
    resetFeedback();

    if (!validateCaptcha()) {
      setError("Please answer the security check correctly.");
      refreshCaptcha();
      return;
    }

    setBusy(true);
    try {
      const response = await forgotPasswordRequest(forgotEmail);
      setMessage(response.message || "If the email is registered, reset instructions will be sent shortly.");
    } catch (requestError) {
      console.error("Forgot password error:", requestError);
      setError("We couldn't send the reset email right now.");
    } finally {
      setBusy(false);
      refreshCaptcha();
    }
  };

  const handleSocialClick = (providerKey) => {
    const provider = socialProviders?.[providerKey];
    if (provider?.enabled && provider.url) {
      window.location.href = provider.url;
    }
  };

  return (
    <div className={`auth-shell auth-shell--${mode}`}>
      <section className="auth-shell__hero">
        <p className="auth-shell__eyebrow">Asher Jewels</p>
        <div className="auth-shell__title-row">
          <span className="auth-shell__icon" aria-hidden="true">*</span>
          <h2>{tab === "register" ? "Join us and start saving your favorites" : "Welcome back to your Asher Jewels account"}</h2>
        </div>
        <p>
          We are happy to see you here. Sign in and enjoy your shopping.
        </p>
        <ul className="auth-shell__benefits">
          <li>Pick up where you left off</li>
          <li>Keep your favorite pieces close</li>
          <li>Enjoy a smooth shopping experience</li>
        </ul>
      </section>

      <section className="auth-card-modern">
        <div className="auth-card-modern__header">
          <div>
            <p className="auth-card-modern__eyebrow">Customer Portal</p>
            <div className="auth-card-modern__title-row">
              <span className="auth-card-modern__title-icon" aria-hidden="true">
                {showForgot ? "@" : tab === "register" ? "+" : "#"}
              </span>
              <h3>{showForgot ? "Reset your password" : tab === "register" ? "Register" : "Login"}</h3>
            </div>
          </div>
          {onClose ? (
            <button type="button" className="auth-card-modern__close" onClick={onClose} aria-label="Close">
              {"\u00D7"}
            </button>
          ) : null}
        </div>

        {visibleSocialProviders.length > 0 ? (
          <>
            <div className="auth-social-row">
              {visibleSocialProviders.map(([key, provider]) => (
                <button key={key} type="button" className={`auth-social-button ${key}`} onClick={() => handleSocialClick(key)}>
                  <span>{key === "google" ? "G" : "f"}</span>
                  {provider.label}
                </button>
              ))}
            </div>
            <div className="auth-divider"><span>or continue with email</span></div>
          </>
        ) : null}

        {!showForgot ? (
          <div className="auth-segmented-control" role="tablist" aria-label="Login or Register">
            <button type="button" className={tab === "login" ? "active" : ""} onClick={() => resetAuthSurface("login")}>Login</button>
            <button type="button" className={tab === "register" ? "active" : ""} onClick={() => resetAuthSurface("register")}>Register</button>
          </div>
        ) : null}

        {error ? <div className="auth-feedback auth-feedback--error">{error}</div> : null}
        {message ? <div className="auth-feedback auth-feedback--success">{message}</div> : null}

        {showForgot ? (
          <form className="auth-modern-form" onSubmit={handleForgotPassword}>
            <label>
              Email address
              <input
                type="email"
                value={forgotEmail}
                onChange={(event) => setForgotEmail(event.target.value)}
                placeholder="you@example.com"
                required
              />
            </label>
            <label>
              Security check
              <div className="auth-security-row">
                <div className="auth-security-chip">{captcha.prompt}</div>
                <input id={`auth-captcha-${mode}`} type="text" inputMode="numeric" placeholder="Answer" required />
              </div>
            </label>
            <button type="submit" className="auth-primary-button" disabled={busy}>
              {busy ? "Sending..." : "Send reset link"}
            </button>
            <button type="button" className="auth-text-button" onClick={() => resetAuthSurface("login")}>
              Back to login
            </button>
          </form>
        ) : tab === "login" ? (
          <form className="auth-modern-form" onSubmit={handleLogin}>
            <label>
              Username or email
              <input
                type="text"
                value={loginData.login_id}
                onChange={(event) => setLoginData((prev) => ({ ...prev, login_id: event.target.value }))}
                placeholder="Username or email"
                required
              />
            </label>
            <label>
              Password
              <input
                type="password"
                value={loginData.password}
                onChange={(event) => setLoginData((prev) => ({ ...prev, password: event.target.value }))}
                placeholder="Enter your password"
                required
              />
            </label>
            <label>
              Security check
              <div className="auth-security-row">
                <div className="auth-security-chip">{captcha.prompt}</div>
                <input id={`auth-captcha-${mode}`} type="text" inputMode="numeric" placeholder="Answer" required />
              </div>
            </label>
            <button type="submit" className="auth-primary-button" disabled={busy}>
              {busy ? "Signing in..." : "Sign in securely"}
            </button>
            <button type="button" className="auth-text-button" onClick={() => { setShowForgot(true); resetFeedback(); refreshCaptcha(); }}>
              Forgot password?
            </button>
          </form>
        ) : (
          <form className="auth-modern-form" onSubmit={handleRegister}>
            <div className="auth-grid-two">
              <label>
                Full name
                <input
                  type="text"
                  value={registerData.full_name}
                  onChange={(event) => setRegisterData((prev) => ({ ...prev, full_name: event.target.value }))}
                  placeholder="Full name"
                  required
                />
              </label>
              <label>
                Mobile number
                <input
                  type="text"
                  value={registerData.contact_number}
                  onChange={(event) => setRegisterData((prev) => ({ ...prev, contact_number: event.target.value }))}
                  placeholder="09XXXXXXXXX"
                  required
                />
              </label>
            </div>
            <label>
              Email address
              <input
                type="email"
                value={registerData.email}
                onChange={(event) => setRegisterData((prev) => ({ ...prev, email: event.target.value }))}
                placeholder="you@example.com"
                required
              />
            </label>
            <label>
              Username
              <input
                type="text"
                value={registerData.username}
                onChange={(event) => setRegisterData((prev) => ({ ...prev, username: event.target.value }))}
                placeholder="Choose a username"
                required
              />
            </label>
            <div className="auth-grid-two">
              <label>
                Password
                <input
                  type="password"
                  value={registerData.password}
                  onChange={(event) => setRegisterData((prev) => ({ ...prev, password: event.target.value }))}
                  placeholder="Create a password"
                  required
                />
              </label>
              <label>
                Confirm password
                <input
                  type="password"
                  value={registerData.password_confirmation}
                  onChange={(event) => setRegisterData((prev) => ({ ...prev, password_confirmation: event.target.value }))}
                  placeholder="Repeat password"
                  required
                />
              </label>
            </div>
            <div className="auth-password-meter">
              <div className={`auth-password-meter__bar ${strength.className}`} />
              <span>{strength.label}</span>
            </div>
            <label>
              Security check
              <div className="auth-security-row">
                <div className="auth-security-chip">{captcha.prompt}</div>
                <input id={`auth-captcha-${mode}`} type="text" inputMode="numeric" placeholder="Answer" required />
              </div>
            </label>
            <label className="auth-checkbox-row">
              <input
                type="checkbox"
                checked={registerData.accept_terms}
                onChange={(event) => setRegisterData((prev) => ({ ...prev, accept_terms: event.target.checked }))}
              />
              <span>I agree to the Terms & Conditions and Privacy Policy.</span>
            </label>
            <button type="submit" className="auth-primary-button" disabled={busy}>
              {busy ? "Creating account..." : "Create account"}
            </button>
          </form>
        )}
      </section>
    </div>
  );
}

export default function LoginRegisterModal({ isOpen, onClose, onSuccess }) {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay auth-modal-overlay" onClick={onClose}>
      <div className="modal-content auth-modal-content" onClick={(event) => event.stopPropagation()}>
        <AuthExperience mode="modal" onClose={onClose} onSuccess={onSuccess} />
      </div>
    </div>
  );
}
